<template>
  <div>
    <page-header
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      v-model="searchModel"
      class="mb-4"
      :filters="filtersConfig"
      @provider="onProviderChange"
      @search="onSearch"
    />
    <custom-separator
      class="mt-1 mb-4"
      text="Resultado de Servicios Compensatorios"
    />
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No hay información disponible.'"
    />
    <div v-else>
      <div v-for="(result, index) in results" :key="index">
        <result-row
          :value="result"
          @on-open-additional-information="
            () =>
              setActiveViewDisplay(
                viewDisplayEnum.ADDITIONAL_INFORMATION,
                index
              )
          "
          @on-open-close="onOpenCloseHandler(index, result)"
          @on-open-documents="onOpenRequestDocuments(index, result)"
          @on-open-request-histories="onOpenRequestStatusLog(index, result)"
          @on-open-return-to-previous-status="
            onOnOpenReturnToPreviousStatus(index)
          "
          @on-show-accept="setActiveViewDisplay(viewDisplayEnum.ACCEPT, index)"
          @on-show-reject="setActiveViewDisplay(viewDisplayEnum.REJECT, index)"
          @on-view-request-aee="onOpenAeeRequest(index)"
          @refresh="onSearch"
        />
      </div>
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="onGoToPage"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ResultRow from './components/ResultRow.vue';
import Pagination from 'pagination';

import {
  getInitFilters as _getInitFilters,
  search as _search,
  getRequestDocumentsByRequestId as _getRequestDocumentsByRequestId,
  getStatusLogByRequestId as _getStatusLogByRequestId,
} from '@/services/provider/extended-academic-year.api';

import { getSpecialistsByProvider as _getSpecialistsByProvider } from '@/services/provider/specialist.api';

import viewDisplay from '@/utils/constants/aeeViewDisplay';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import roles from '@/utils/constants/roles.js';

export default {
  name: 'AeeQuestionnaireSearch',
  mixins: [storeMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ResultRow,
    Pagination,
  },
  data() {
    return {
      searchModel: {
        academicYear: null,
        status: null,
        searchText: null,
        requestType: null,
        disciplineType: null,
        provider: null,
        specialist: null,
        origin: null,
      },
      filterOptions: {
        academicYears: [],
        statuses: [],
        requestTypes: [],
        disciplines: [],
        providers: [],
        specialists: [],
        origins: [
          { id: 'SAEE', name: 'SAEE' },
          { id: 'RP', name: 'RP' },
        ],
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: 10,
      },
      results: [],
    };
  },
  computed: {
    viewDisplayEnum() {
      return viewDisplay;
    },
    mainCompany() {
      return this.userMainCompany ?? this.userCompanies.length > 0
        ? this.userCompanies[0]
        : null;
    },
    headerConfig() {
      return {
        title: 'Manejo de Cuestionarios de Año Escolar Extendido',
        icon: 'fas fa-user-tie',
        routes: [
          'Proveedor',
          'Manejo de Cuestionarios de Año Escolar Extendido',
        ],
      };
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          clearable: true,
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          placeholder:
            'Escriba Número de Solicitud, Nombre del Estudiante o SIE',
          value: this.searchModel.input,
          class: 'col-12 col-md-6',
        },
        {
          name: 'requestType',
          component: 'CustomDropDown',
          options: this.filterOptions.requestTypes,
          label: 'Tipo de Solicitud:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          disabled: this.filterOptions.requestTypes.length === 1,
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplines,
          label: 'Disciplina:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          label: 'Proveedor:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          disabled: this.userIsInRoles(),
        },
        {
          name: 'specialist',
          component: 'CustomDropDown',
          options: this.filterOptions.specialists,
          label: 'Especialista:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'origin',
          component: 'CustomDropDown',
          options: this.filterOptions.origins,
          label: 'Procedencia:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          clearable: true,
        },
      ];
    },
  },
  methods: {
    async onProviderChange(value) {
      if (this.searchModel.academicYear) {
        await _getSpecialistsByProvider(
          this.searchModel.academicYear.id,
          value.id,
          true
        ).then(({ data }) => {
          this.filterOptions.specialists = data;
        });
      }
    },
    async onSearch() {
      let payload = {
        academicYearId: this.searchModel.academicYear?.id,
        statusId: this.searchModel.status?.id,
        searchText: this.searchModel.searchText,
        requestTypeId: this.searchModel.requestType?.id,
        disciplineTypeId: this.searchModel.disciplineType?.id,
        providerId: this.searchModel.provider?.id,
        origin: this.searchModel.origin?.id,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };

      await _search(payload).then(({ data }) => {
        this.results = data.data.map((d) => ({
          ...d,
          isOpen: false,
          viewToDisplay: this.viewDisplayEnum.UNSET,
          statusLogs: [],
          documents: [],
        }));

        this.pagination.count = data.count;
        this.pagination.currentPage = data.currentPage;
        this.pagination.pageCount = data.pageCount;
        this.pagination.totalItemCount = data.totalItemCount;
      });
    },
    onOpenCloseHandler(index, row) {
      if (row.isOpen) {
        this.setActiveViewDisplay(this.viewDisplayEnum.UNSET, index, {});
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: false,
        }));
      } else {
        this.onOpenAdditionalInfoTabHandler(index);
        this.results = this.results.map((x, rowIndex) => ({
          ...x,
          isOpen: rowIndex === index,
        }));
      }
    },
    async onOpenAdditionalInfoTabHandler(index) {
      this.setActiveViewDisplay(
        this.viewDisplayEnum.ADDITIONAL_INFORMATION,
        index,
        {}
      );
    },
    async onOpenRequestDocuments(index, item) {
      await _getRequestDocumentsByRequestId(
        item.academicYearId,
        item.requestId
      ).then(({ data }) => {
        item.documents = data;
        this.setActiveViewDisplay(this.viewDisplayEnum.DOCUMENTS, index);
      });
    },
    async onOpenRequestStatusLog(index, item) {
      await _getStatusLogByRequestId(item.requestId).then(({ data }) => {
        item.statusLogs = data;
        this.setActiveViewDisplay(this.viewDisplayEnum.HISTORY_REQUEST, index);
      });
    },
    async onOpenAeeRequest(index) {
      this.setActiveViewDisplay(this.viewDisplayEnum.AEE_REQUEST, index);
    },
    setActiveViewDisplay(viewDisplay, index, prop) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isOpen: idx === index,
        viewToDisplay: idx === index ? viewDisplay : this.viewDisplayEnum.UNSET,
        ...(idx === index ? prop : {}),
      }));
    },
    async onGoToPage(page) {
      this.pagination.currentPage = page;
      this.onSearch();
    },
    userIsInRoles() {
      return (
        (this.userRoleId === roles.Provider_RP ||
          this.userRoleId == roles.Specialist_RP ||
          this.userRoleId === roles.Assistant_Provider_RP) &&
        this.mainCompany != null
      );
    },
    async onOnOpenReturnToPreviousStatus(index) {
      this.setActiveViewDisplay(this.viewDisplayEnum.REVERT, index);
    },
  },
  async mounted() {
    await _getInitFilters().then(async ({ data }) => {
      this.filterOptions.academicYears = data.academicYears;
      this.filterOptions.statuses = data.statuses;
      this.filterOptions.requestTypes = data.requestTypes;
      this.filterOptions.disciplines = data.disciplines;
      this.filterOptions.providers = data.providers;

      if (this.filterOptions.academicYears.length > 0) {
        this.searchModel.academicYear = this.filterOptions.academicYears[0];
      }

      if (this.filterOptions.requestTypes.length === 1) {
        this.searchModel.requestType = this.filterOptions.requestTypes[0];
      }

      if (this.userIsInRoles()) {
        this.filterOptions.providers = [this.mainCompany];
        this.searchModel.provider = this.filterOptions.providers[0];
        await this.onProviderChange(this.searchModel.provider);
      }
    });
  },
};
</script>
